import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@pairy/pds-react';
import cn from 'classnames';

import { EmbedType } from '../../../../PdfViewer/index.types';
import {
  getEmbedIframeSrc,
  normalizeEmbedOptions,
} from '../../../../../utils/embed';
import * as Styled from '../index.styles';

interface Props {
  embed: EmbedType;
  /* 가림막/문의양식 게이트로 가려진 상태 — 블러된 썸네일만 보여주고 재생(자동재생 포함)하지 않는다 */
  isCovered?: boolean;
}

/* 재생 이벤트가 오지 않는 경우(자동재생 차단 등)에 대비한 커버 해제 폴백 시간 */
const REVEAL_FALLBACK_MS = 3000;

const EmbedVideo = ({ embed, isCovered = false }: Props) => {
  const options = normalizeEmbedOptions(embed.options);
  const [isActivated, setIsActivated] = useState(
    options.autoplay && !isCovered,
  );
  const [isActivatedByClick, setIsActivatedByClick] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fallbackTimerRef = useRef<number | null>(null);

  /* 가림막이 해제되면 그때 자동재생을 시작한다 */
  useEffect(() => {
    if (!isCovered && options.autoplay && !isActivated) {
      setIsActivated(true);
    }
  }, [isCovered, options.autoplay, isActivated]);

  /**
   * 플레이어 로딩 화면까지 썸네일로 가리기 위해, iframe 로드 시점이 아닌
   * 실제 재생 시작(postMessage 이벤트) 시점에 커버를 걷어낸다.
   * - YouTube: enablejsapi=1 + listening 등록 → infoDelivery의 playerState 1(재생)
   * - Vimeo: addEventListener 등록 → play 이벤트
   */
  useEffect(() => {
    if (!isActivated || isPlayerReady) {
      return;
    }

    const handleMessage = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) {
        return;
      }

      let data = e.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }

      if (data?.info?.playerState === 1 || data?.event === 'play') {
        setIsPlayerReady(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (fallbackTimerRef.current !== null) {
        window.clearTimeout(fallbackTimerRef.current);
      }
    };
  }, [isActivated, isPlayerReady]);

  const handleIframeLoad = () => {
    const contentWindow = iframeRef.current?.contentWindow;
    if (contentWindow) {
      if (embed.provider === 'youtube') {
        contentWindow.postMessage(
          JSON.stringify({
            event: 'listening',
            id: embed.id,
            channel: 'widget',
          }),
          '*',
        );
      } else {
        contentWindow.postMessage(
          JSON.stringify({ method: 'addEventListener', value: 'play' }),
          '*',
        );
      }
    }

    fallbackTimerRef.current = window.setTimeout(() => {
      setIsPlayerReady(true);
    }, REVEAL_FALLBACK_MS);
  };

  const handlePlayButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsActivatedByClick(true);
    setIsActivated(true);
  };

  if (isCovered || !isActivated) {
    return (
      <Styled.Facade
        type="button"
        aria-label={embed.title ?? '동영상 재생'}
        className={cn({ 'is-covered': isCovered })}
        onClick={handlePlayButtonClick}
        disabled={isCovered}
      >
        {embed.thumbnailUrl && (
          <img src={embed.thumbnailUrl} alt="" draggable={false} />
        )}
        {!isCovered && (
          <Styled.PlayButton>
            <Icon icon="ph:play-fill" size={24} />
          </Styled.PlayButton>
        )}
      </Styled.Facade>
    );
  }

  return (
    <>
      <iframe
        ref={iframeRef}
        src={getEmbedIframeSrc({
          provider: embed.provider,
          videoId: embed.videoId,
          // NOTE: 클릭으로 활성화된 경우 바로 재생을 시작한다 (사용자 제스처라 mute 강제 불필요)
          options: isActivatedByClick
            ? { ...options, autoplay: true }
            : options,
        })}
        title={embed.title ?? 'video player'}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
        onClick={(e) => e.stopPropagation()}
        onLoad={handleIframeLoad}
      />
      {/* 재생이 시작될 때까지 검은 화면/플레이어 로딩 화면 대신 썸네일 + 로딩 스피너를 유지한다 */}
      <Styled.LoadingCover className={cn({ 'is-hidden': isPlayerReady })}>
        {embed.thumbnailUrl && (
          <img src={embed.thumbnailUrl} alt="" draggable={false} />
        )}
        <Styled.LoadingSpinner>
          <Icon icon="ph:circle-notch" size={24} />
        </Styled.LoadingSpinner>
      </Styled.LoadingCover>
    </>
  );
};

export default EmbedVideo;
